/* eslint-disable react/jsx-wrap-multilines */
import styled from '@emotion/styled';
import { useState } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import TextField from '@base/TextField/TextField';
import useInterval from '@hooks/useInterval';
import { Spacings, Fonts } from '@styles/index';
import useLoginOTPMessage from '@cont/Login/utils/hooks/useLoginOTPMessage';

const StyledP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const StyledTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_4B};
`;

const DisabledTextField = styled(TextField)`
  background: var(--color-divider);
  &.MuiInputBase-root.Mui-disabled input {
    color: var(--color-text-secondary);
  }
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
  min-width: ${({ fixedWidth }) => (fixedWidth ? '140px' : '28px')};
`;

const StyledH6WithMargins = styled(H6)`
  font-weight: ${Fonts.BOLD};
  margin: 0 ${Spacings.SPACING_2B};
  padding-bottom: ${Spacings.SPACING_2B};
`;

const StyledUnderlineLink = styled(UnderlineLink)`
  border-bottom-width: 0;
  padding-bottom: 0;
`;

const UnderlineLinkWrapper = styled.div`
  position: absolute;
  right: ${Spacings.SPACING_4B};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EditDiv = styled.div`
  position: relative;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin-top: ${Spacings.SPACING_8B};
`;

const StyledSelectedMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: ${Spacings.SPACING_2B};
`;

const StyledImg = styled.img`
  width: ${Spacings.SPACING_6B};
`;

const BoldP1 = styled(P1)`
  padding: 0 ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_3B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const StyledP2 = styled.p`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.P1};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  margin: ${Spacings.SPACING_0B};
  color: ${({ color }) => color};
  margin-top: ${Spacings.SPACING_3B};
`;

const EnterOtpStep = ({
  mobileNumber,
  country,
  otp,
  setOtp,
  code,
  setCode,
  resendOtp,
  editMobileNumber,
  otpOrEmailResponse,
  submitLogin,
  loggingIn,
  errorMsg,
  isRegistered
}) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const startTimer = () => {
    setTimeLeft(60);
  };
  const onOtpChange = (e) => {
    const otpFilled = e.target.value.replace(/ /g, '');
    if (otpFilled.match(/^\d+$/) || otpFilled === '') {
      setOtp(otpFilled);
    }
  };

  const onCodeChange = (e) => {
    setCode(e.target.value);
  };

  const resend = (e) => {
    if (e) e.preventDefault();
    if (timeLeft === 0) {
      startTimer();
      resendOtp(1);
    }
  };
  const onCall = (e) => {
    if (e) e.preventDefault();
    if (timeLeft === 0) {
      startTimer();
      resendOtp(2);
    }
  };

  const shouldEnableSubmit = () => {
    let enabled = true;
    if (otp === '') {
      enabled = false;
    }
    if (!otpOrEmailResponse.is_un_teach_user && code.trim === '') {
      enabled = false;
    }
    return enabled;
  };

  const onButtonClick = (e) => {
    e.preventDefault();
    if (shouldEnableSubmit()) submitLogin();
  };
  const btnLabel = 'Verify OTP';
  const subHeading = useLoginOTPMessage();

  const getTimeLeftStr = () => {
    if (timeLeft > 0) {
      const str = `0${timeLeft}`;
      return ` in 00:${String(str).slice(-2)}`;
    }
    return '';
  };

  useInterval(
    () => {
      setTimeLeft(timeLeft - 1);
    },
    timeLeft > 0 ? 1000 : null
  );
  return (
    <>
      <H2>Enter OTP</H2>
      <StyledP1>{subHeading}</StyledP1>
      {mobileNumber !== '' ? (
        <EditDiv>
          <DisabledTextField
            startAdornment={
              <InputAdornment>
                <StyledSelectedMenu>
                  <StyledImg src={country.flagIconUrl} alt={country.name} />
                  <BoldP1>{`+${country.phoneCode}`}</BoldP1>
                </StyledSelectedMenu>
              </InputAdornment>
            }
            fullWidth
            value={mobileNumber}
            type="tel"
            disabled
            endAdornment={
              <StyledUnderlineLink onClick={editMobileNumber} href="#">
                <StyledH6>Edit</StyledH6>
              </StyledUnderlineLink>
            }
          />
        </EditDiv>
      ) : null}
      <form onSubmit={onButtonClick}>
        <StyledTextField
          fullWidth
          value={otp}
          type="tel"
          placeholder="One time password"
          onChange={onOtpChange}
          autoFocus
          inputProps={{
            type: 'tel',
            maxLength: 6,
            minLength: 4,
            required: true
          }}
          // error={errorMsg !== ''}
          // helperText={errorMsg}
        />
        {!otpOrEmailResponse.is_un_teach_user && isRegistered ? (
          <StyledTextField
            fullWidth
            value={code}
            type="text"
            placeholder="Institute code"
            onChange={onCodeChange}
          />
        ) : null}
      </form>
      {errorMsg && errorMsg !== '' ? (
        <StyledP2 color="var(--color-i-red)">{errorMsg}</StyledP2>
      ) : null}
      <ButtonWrapper>
        <Button
          variant={shouldEnableSubmit() && !loggingIn ? 'filled' : 'disabled'}
          label={loggingIn ? 'Submitting...' : btnLabel}
          onClick={onButtonClick}
          color="blue"
        />
        <UnderlineLinkWrapper>
          <UnderlineLink disabled={timeLeft > 0} onClick={resend} href="#">
            <StyledH6
              color={
                timeLeft > 0
                  ? 'var(--color-text-secondary)'
                  : 'var(--color-text-primary)'
              }
              fixedWidth={timeLeft > 0}
            >
              {`Resend OTP ${getTimeLeftStr()}`}
            </StyledH6>
          </UnderlineLink>
          {timeLeft === 0 ? (
            <StyledH6WithMargins color="var(--color-text-secondary)">
              or
            </StyledH6WithMargins>
          ) : null}
          {timeLeft === 0 ? (
            <UnderlineLink onClick={onCall} href="#">
              <StyledH6>Call</StyledH6>
            </UnderlineLink>
          ) : null}
        </UnderlineLinkWrapper>
      </ButtonWrapper>
    </>
  );
};

export default EnterOtpStep;

EnterOtpStep.propTypes = {
  mobileNumber: PropTypes.string.isRequired,
  country: PropTypes.objectOf(PropTypes.any).isRequired,
  otp: PropTypes.string.isRequired,
  setOtp: PropTypes.func.isRequired,
  code: PropTypes.string.isRequired,
  setCode: PropTypes.func.isRequired,
  resendOtp: PropTypes.func.isRequired,
  editMobileNumber: PropTypes.func.isRequired,
  submitLogin: PropTypes.func.isRequired,
  loggingIn: PropTypes.bool.isRequired,
  otpOrEmailResponse: PropTypes.objectOf(PropTypes.any).isRequired,
  errorMsg: PropTypes.string.isRequired,
  isRegistered: PropTypes.bool.isRequired
};

EnterOtpStep.defaultProps = {};
